/**
 * Task Field Visibility Configuration
 * Defines which fields should be visible/required for each task type
 */

// Field mapping for each task type
// Note: Field names include both variations (e.g., task_type/type, due_date/targetdate) for compatibility
const TASK_TYPE_FIELD_MAPPING = {
  Call: {
    visible: ['subject', 'title', 'due_date', 'due_time', 'related_to', 'related_object', 'related_record_id', 'notes', 'description', 'task_type', 'type', 'status', 'priority', 'ownerid'],
    required: ['subject', 'title']
  },

  Email: {
    visible: ['subject', 'title', 'due_date', 'due_time', 'related_to', 'related_object', 'related_record_id', 'description', 'task_type', 'type', 'status', 'priority', 'ownerid', 'toemail', 'fromemail', 'ccemail'],
    required: ['subject', 'title']
  },
  'Follow-up': {
    visible: ['subject', 'title', 'due_date', 'due_time', 'related_to', 'related_object', 'related_record_id', 'description', 'task_type', 'type', 'status', 'priority', 'ownerid', 'toemail', 'fromemail', 'ccemail'],
    required: ['subject', 'title']
  },
  'To-Do': {
    visible: ['subject', 'title', 'due_date', 'due_time', 'targetdate', 'description', 'task_type', 'type', 'status', 'priority', 'ownerid'],
    required: ['subject', 'title']
  },
  Meeting: {
    visible: ['subject', 'title', 'start_date_time', 'end_date_time', 'meeting_start_datetime', 'meeting_end_datetime',
      'start_datetime', 'startdatetime', 'end_datetime', 'enddatetime',
      'location', 'related_to', 'related_object', 'related_record_id',
      'description', 'task_type', 'type', 'status', 'priority', 'ownerid', 'attendees', 'attendee_ids'],
    required: ['subject', 'title', 'start_date_time', 'end_date_time']
  }
};

/**
 * Check if a field should be visible for a given task type
 * @param {string} fieldName - The name of the field to check
 * @param {string} taskType - The current task type
 * @param {Object} formData - Optional form data to check conditional visibility (e.g., reminder_type)
 * @returns {boolean} - True if the field should be visible
 */
export const isFieldVisible = (fieldName, taskType, formData = {}) => {
  if (!fieldName) return false;

  // Normalize field name to lowercase for comparison
  const normalizedFieldName = fieldName.toLowerCase();

  // Special handling for Reminder task type with conditional field visibility

  // Get reminder_type value
  const reminderType = formData?.reminder_type || '';

  // Normalize the reminder_type value for comparison
  const normalizedType = String(reminderType).toLowerCase().trim();

  // Check if reminder_type indicates "specific date" or "based on record field"
  const isSpecificDate = normalizedType === 'specific date';
  const isBasedOnField = normalizedType === 'based on record field';

  // Always show reminder_type field so user can select the type
  if (normalizedFieldName === 'reminder_type') {
    return true;
  }

  // Field-specific visibility logic for Reminder type
  if (normalizedFieldName === 'reminder_date_time') {
    // Show reminder_date_time only if reminder_type is "specific date"
    return isSpecificDate;
  }

  // Show these fields only if reminder_type is NOT "specific date"
  // This allows them to show when:
  // - reminder_type is "based on record field" (explicit condition)
  // - reminder_type is not set yet (so dependent fields can show based on their controlling field, e.g., "type")
  // Note: These fields can also be dependent picklist fields controlled by other fields (e.g., type)
  // Dependent fields should be visible when their controlling field has a value
  if (normalizedFieldName === 'reminder_execution' ||
    normalizedFieldName === 'reminder_reference_field' ||
    normalizedFieldName === 'reminder_unit' ||
    normalizedFieldName === 'reminder_value') {
    // Show if reminder_type is NOT "specific date" (i.e., show when "based on record field" or not set)
    // This allows dependent fields controlled by "type" to be visible
    return !isSpecificDate;
  }


  // If no task type is selected, show all fields
  if (!taskType) {
    return true;
  }

  // Check if this field is controlled by ANY task type mapping
  // A field is "controlled" if it appears in at least one task type's visible list
  const isControlledField = Object.values(TASK_TYPE_FIELD_MAPPING).some(config =>
    config.visible.some(visibleField =>
      normalizedFieldName === visibleField.toLowerCase()
    )
  );

  // If field is NOT controlled by any task type mapping, show it by default
  // (Uncontrolled fields are visible for all task types)
  if (!isControlledField) {
    return true;
  }

  // Field IS controlled - check if it's in the current task type's visible list
  const typeConfig = TASK_TYPE_FIELD_MAPPING[taskType];

  // If task type not found in mapping, show controlled fields by default
  // (This shouldn't happen often, but for safety)
  if (!typeConfig) {
    return true;
  }

  // Check if field is in the visible list for the current task type
  const isInCurrentTaskTypeVisibleList = typeConfig.visible.some(visibleField =>
    normalizedFieldName === visibleField.toLowerCase()
  );

  // Only show the field if it's in the current task type's visible list
  return isInCurrentTaskTypeVisibleList;
};

/**
 * Check if a field should be required for a given task type
 * @param {string} fieldName - The name of the field to check
 * @param {string} taskType - The current task type
 * @param {boolean} baseRequired - The base required state from field config
 * @returns {boolean} - True if the field should be required
 */
export const isFieldRequired = (fieldName, taskType, baseRequired = false) => {
  if (!fieldName || !taskType) return baseRequired;

  // Normalize field name to lowercase for comparison
  const normalizedFieldName = fieldName.toLowerCase();

  // Get the field visibility configuration for this task type
  const typeConfig = TASK_TYPE_FIELD_MAPPING[taskType];

  // If task type not found in mapping, use base required
  if (!typeConfig) {
    return baseRequired;
  }

  // Check if field is in the required list for this task type
  const isRequiredByType = typeConfig.required.some(requiredField =>
    normalizedFieldName === requiredField.toLowerCase()
  );

  // Field is required if it's required by type OR by base config
  return isRequiredByType || baseRequired;
};

/**
 * Filter fields array based on task type visibility
 * @param {Array} fields - Array of field objects
 * @param {string} taskType - The current task type
 * @returns {Array} - Filtered array of visible fields
 */
export const filterFieldsByTaskType = (fields, taskType) => {
  if (!fields || !Array.isArray(fields)) return [];

  return fields.filter(field => isFieldVisible(field.fieldName, taskType));
};

/**
 * Filter sections and their fields based on task type
 * Returns only sections that have at least one visible field
 * @param {Array} sections - Array of section objects with fields
 * @param {string} taskType - The current task type
 * @returns {Array} - Sections with filtered fields (empty sections are excluded)
 */
export const filterSectionsByTaskType = (sections, taskType) => {
  if (!sections || !Array.isArray(sections)) return [];

  return sections
    .map(section => ({
      ...section,
      fields: filterFieldsByTaskType(section.fields, taskType)
    }))
    .filter(section => section.fields && section.fields.length > 0); // Hide sections with no visible fields
};

/**
 * Check if a section has any visible fields for the given task type
 * @param {Object} section - Section object with fields array
 * @param {string} taskType - The current task type
 * @returns {boolean} - True if section has at least one visible field
 */
export const hasSectionVisibleFields = (section, taskType) => {
  if (!section || !section.fields || !Array.isArray(section.fields)) return false;

  return section.fields.some(field => isFieldVisible(field.fieldName, taskType));
};

/**
 * Get the visible fields list for a task type
 * @param {string} taskType - The task type
 * @returns {Array} - Array of visible field names
 */
export const getVisibleFieldsForTaskType = (taskType) => {
  if (!taskType) return [];

  const typeConfig = TASK_TYPE_FIELD_MAPPING[taskType];
  return typeConfig ? typeConfig.visible : [];
};

export default {
  isFieldVisible,
  isFieldRequired,
  filterFieldsByTaskType,
  filterSectionsByTaskType,
  hasSectionVisibleFields,
  getVisibleFieldsForTaskType,
  TASK_TYPE_FIELD_MAPPING
};
